import { CommonModule, DOCUMENT, isPlatformServer, NgOptimizedImage } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  model,
  NgModule,
  OnDestroy,
  PLATFORM_ID,
  signal,
  WritableSignal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgxsModule, select, Store } from '@ngxs/store';
import {
  AnimateModule,
  debouncedSignal,
  PaginatorComponent,
  TrainingBrand,
  TrainingFormat,
  TrainingType,
  WwwAppState,
  WwwRouteSlug,
} from '@pro-connect/shared';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { ITrainingsListDto } from '../../models/trainings-list-dto';
import { TrainingTileComponent } from './components/training-tile/training-tile.component';
import { TrainingsFilterComponent } from './components/trainings-filter/trainings-filter.component';
import {
  TrainingsListState,
  TrainingsListStateGetCategories,
  TrainingsListStateGetCities,
  TrainingsListStateGetEducators,
  TrainingsListStateGetPlaces,
  TrainingsListStateGetTrainings,
  TrainingsListStateNullifyInit,
} from './trainings-list.state';
import { TrainingsCategoriesComponent } from './components/trainings-categories/trainings-categories.component';
import { ActivatedRoute, Params, Router } from '@angular/router';

export interface IFilter {
  category_id: number | null;
  sub_category_id: number | null;
  format: TrainingFormat | null;
  brand: TrainingBrand | null;
  price_from: number | null;
  price_to: number | null;
  place_id: number | null;
  educator_id: number | null;
  date_from: string | null;
  date_to: string | null;
  city: string | null;
  payment: 'free' | 'paid' | null;
  direction?: 'asc' | 'desc';
  sort?: string;
}
@NgModule({
  imports: [NgxsModule.forFeature([TrainingsListState])],
})
class TrainingsListStateLazyModule {}

@Component({
  selector: 'app-trainings-list',
  templateUrl: './trainings-list.component.html',
  styleUrls: ['./trainings-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    TrainingsListStateLazyModule,
    TrainingTileComponent,
    TrainingsFilterComponent,
    PaginatorComponent,
    NgOptimizedImage,
    NgxSkeletonLoaderModule,
    FormsModule,
    AnimateModule,
    TrainingsCategoriesComponent,
  ],
})
export class TrainingsListComponent {
  public readonly DEBOUNCE_TIME = 300;
  public readonly sortTable: { value: string; name: string }[] = [
    { value: 'price', name: 'Cena' },
    { value: 'name', name: 'Nazwa' },
  ];

  public readonly trainings = select(TrainingsListState.list);
  public readonly categories = select(TrainingsListState.categories);
  public readonly subCategories = select(TrainingsListState.subCategories);
  public readonly educators = select(TrainingsListState.educators);
  public readonly places = select(TrainingsListState.places);
  public readonly cities = select(TrainingsListState.cities);
  public readonly loading = select(TrainingsListState.loading);
  public readonly totalPages = select(TrainingsListState.totalPages);
  public readonly maxRangeValue = select(TrainingsListState.maxRangeValue);
  public readonly user = select(WwwAppState.user);

  public readonly showPagination = input<boolean>(true);
  public readonly trainingType = input<TrainingType | null>(null);
  public readonly trainingBrand = input<TrainingBrand | null>(null);
  public readonly perPage = input<number>(18);
  public readonly scrollXOnMobile = input<boolean>(false);
  public readonly isBrandFilterVisible = input<boolean>(true);
  public readonly isCategoriesFilterVisible = input<boolean>(true);
  public readonly showPrices = input<boolean>(true);
  public readonly emptyListText = input<string>('Brak szkoleń');
  public readonly initCategory = input<number>();
  public readonly isTrainingCategoriesFilterVisible = input<boolean>(true);

  public readonly router = inject(Router);
  public readonly activatedRoute = inject(ActivatedRoute);

  public readonly search = model<string | null>(null);
  public readonly debouncedSearch = debouncedSignal(this.search, this.DEBOUNCE_TIME);
  public readonly page = model<number>(1);
  public readonly debouncedPage = debouncedSignal(this.page, this.DEBOUNCE_TIME);
  public readonly filtersOpened: WritableSignal<boolean> = signal(false);

  public categoryIdChange: WritableSignal<number | null> = signal(null);
  public subCategoryIdChange: WritableSignal<number | null> = signal(null);
  public blockScrollOnInit: boolean = true;

  public readonly filters = signal<IFilter>({
    category_id: null,
    sub_category_id: null,
    format: null,
    brand: null,
    price_from: null,
    price_to: null,
    place_id: null,
    educator_id: null,
    date_from: null,
    date_to: null,
    city: null,
    payment: null,
  });
  public readonly listDto = computed<ITrainingsListDto>(() => {
    return {
      search: this.debouncedSearch(),
      page: this.debouncedPage(),
      perPage: this.perPage(),
      format: this.filters().format,
      training_type: this.trainingType(),
      brand: this.trainingBrand() || this.filters().brand,
      category_id: this.initCategory() || this.filters().category_id,
      sub_category_id: this.filters().sub_category_id,
      target: !this.user() ? 'none' : this.user()!.type,
      price_from: this.filters().price_from,
      price_to: this.filters().price_to,
      place_id: this.filters().place_id,
      educator_id: this.filters().educator_id,
      date_from: this.filters().date_from,
      date_to: this.filters().date_to,
      direction: this.filters().direction,
      sort: this.filters().sort,
      city: this.filters().city,
      payment: this.filters().payment,
    };
  });
  protected readonly WwwRouteSlug = WwwRouteSlug;
  private readonly _store: Store = inject(Store);
  private readonly _document: Document = inject(DOCUMENT);
  private readonly _platformId = inject(PLATFORM_ID);

  constructor() {
    effect(() => {
      if (this.listDto()) {
        this._store.dispatch([
          new TrainingsListStateGetCategories(),
          new TrainingsListStateGetPlaces(),
          new TrainingsListStateGetEducators(),
          new TrainingsListStateGetCities(),
          new TrainingsListStateGetTrainings({
            listDto: this.listDto(),
          }),
        ]);
      }
    });
  }

  public onFilterValueChange(event: {
    category_id: number | null;
    sub_category_id: number | null;
    format: TrainingFormat | null;
    brand: TrainingBrand | null;
    price_from: number | null;
    price_to: number | null;
    place_id: number | null;
    educator_id: number | null;
    date_from: string | null;
    date_to: string | null;
    direction: 'asc' | 'desc' | undefined;
    sort: string | undefined;
    city: string | null;
    payment: 'free' | 'paid' | null;
  }): void {
    this.filters.set(event);

    this.onCategoryFilterValueChange({ category_id: event.category_id, sub_category_id: event.sub_category_id });

    // this._regenerateUrlParams();
  }

  public onCategoryFilterValueChange(event: { category_id: number | null; sub_category_id: number | null }): void {
    this.categoryIdChange.set(event.category_id);
    this.subCategoryIdChange.set(event.sub_category_id);
  }

  public pageChange(event: { page: number }): void {
    this.page.set(event.page);
    // setTimeout(() => {
    //   if (isPlatformServer(this._platformId)) {
    //     return;
    //   }
    //   if (!this.blockScrollOnInit) {
    //     this._document.getElementById('trainingsList')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    //   }
    //   this.blockScrollOnInit = false;
    // }, 300);
  }

  public _regenerateUrlParams(): void {
    const keys: string[] = Object.keys(this.filters());
    const queryParams: Params = {};
    keys.forEach((key: string) => {
      if (this.filters()[key as keyof IFilter]) {
        queryParams[key] = this.filters()[key as keyof IFilter];
      }
    });

    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams,
      queryParamsHandling: 'merge',
    });
  }
}
